/**
 * @file src/lib/queryClient.js
 * @description React Query client — single instance for the entire app.
 *
 * Responsibilities:
 *  - Configure global defaults for all queries and mutations
 *  - Global error handler — surfaces ApiError messages consistently
 *  - No persistence (removed PersistQueryClientProvider — see reasoning below)
 *
 * Why we removed persistence:
 *  - Previously the ENTIRE React Query cache was persisted to localStorage
 *    via PersistQueryClientProvider. This meant stale invoice data, company
 *    data, and auth state survived hard refreshes and could show wrong UI.
 *  - The only data that genuinely needs cross-session survival is the
 *    invoice processing progress — handled explicitly in the
 *    invoice-processing-queue page via the InvoiceAutoSave pattern
 *    (keepalive fetch on beforeunload + restore on mount).
 *  - Everything else should re-fetch fresh on mount.
 *
 * Rules:
 *  - This file exports ONE queryClient instance
 *  - No React, no axios, no page imports
 *  - Import { queryClient } from "lib/queryClient" wherever needed
 */

import { QueryClient } from "@tanstack/react-query";
import { DEFAULT_STALE_TIME, DEFAULT_GC_TIME } from "config/constants";
import { ApiError } from "lib/axios";

// ---------------------------------------------------------------------------
// Global error handler
// ---------------------------------------------------------------------------

/**
 * Called for every failed query and mutation unless the individual
 * hook overrides onError.
 *
 * In production: send to your error monitoring service (e.g. Sentry).
 * In development: log to console with full detail.
 */
const handleGlobalError = (error) => {
  if (import.meta.env.DEV) {
    if (error instanceof ApiError) {
      console.error(
        `[API Error] ${error.status} ${error.code}: ${error.message}`,
        {
          validationErrors: error.validationErrors,
          uuid: error.uuid,
          invoiceNumber: error.invoiceNumber,
          invoiceCounterValue: error.invoiceCounterValue,
        },
      );
    } else {
      console.error("[Unexpected Error]", error);
    }
  }

  // TODO: replace with your error monitoring service when domain is live
  // Example: Sentry.captureException(error);
};

// ---------------------------------------------------------------------------
// Query client
// ---------------------------------------------------------------------------

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Data is considered fresh for 10 minutes.
       * Components mounting within this window won't trigger a refetch.
       * Override per-query where data changes more frequently.
       */
      staleTime: DEFAULT_STALE_TIME,

      /**
       * Inactive cache entries are garbage collected after 30 minutes.
       */
      gcTime: DEFAULT_GC_TIME,

      /**
       * Retry once on failure — enough to handle transient network blips
       * without hammering the server on genuine errors.
       * Auth errors (401) are handled by the axios interceptor and should
       * not be retried at the query level.
       */
      retry: (failureCount, error) => {
        // Never retry auth errors — the interceptor handles 401
        if (error instanceof ApiError && error.status === 401) return false;
        // Never retry client errors (4xx) — they won't resolve on retry
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500
        ) {
          return false;
        }
        // Retry once for server errors (5xx) and network errors
        return failureCount < 1;
      },

      /**
       * Refetch when window regains focus so data stays fresh
       * after the user switches tabs.
       */
      refetchOnWindowFocus: true,

      /**
       * Do not refetch on reconnect by default — the user can
       * manually refresh if needed. Override per-query for real-time data.
       */
      refetchOnReconnect: false,

      /**
       * Global query error handler.
       */
      onError: handleGlobalError,
    },

    mutations: {
      /**
       * Mutations are not retried — a failed write should surface
       * immediately so the user can take action.
       */
      retry: false,

      /**
       * Global mutation error handler.
       */
      onError: handleGlobalError,
    },
  },
});

export default queryClient;
